import api, { uploadApi } from "../utils/api";

/* ============================
   READ
============================ */

export const getProjectListByUser = async (userId: number) => {
  try {
    const res = await api.get(`/api/projects`, {
      params: {
        populate: "*",
        sort: "createdAt:desc",
        filters: {
          author: { id: { $eq: userId } },
        },
      },
    });

    return res;
  } catch (e) {
    console.error(e);
    throw e;
  }
};

export const getProjectById = async (projectId: string) => {
  try {
    const res = await api.get(`/api/projects/${projectId}`, {
      params: {
        populate: ["deriFile", "image", "likes", "author", "categories"],
      },
    });

    return res.data.data;
  } catch (e) {
    console.error(e);
    throw e;
  }
};

export const getRelatedProjects = async (
  categories: string[],
  projectId: string
) => {
  try {
    const res = await api.get(`/api/projects`, {
      params: {
        filters: {
          documentId: { $ne: projectId },
          categories: {
            title: { $in: categories },
          },
        },
        populate: ["deriFile", "image", "likes"],
        sort: ["createdAt:desc"],
        pagination: { limit: 3 },
      },
    });

    return res.data.data;
  } catch (e) {
    console.error(e);
    throw e;
  }
};

/* ============================
   UPDATE
============================ */

export const editProject = async (project: project, isTrustedUser: boolean) => {
  try {
    let uploadedDeriFileId: number | null = null;
    let uploadedImageFileId: number | null = null;

    /* ---- Upload new files (if any) ---- */
    if (project.deriFile || project.image) {
      const formData = new FormData();

      if (project.deriFile) formData.append("files", project.deriFile);
      if (project.image) formData.append("files", project.image);

      const uploadRes = await uploadApi.post("/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      uploadRes.data.forEach((file: any) => {
        if (file.mime?.startsWith("image/")) {
          uploadedImageFileId = file.id;
        } else {
          uploadedDeriFileId = file.id;
        }
      });
    }

    /* ---- Remove existing files if deleted ---- */
    if (!project.deriFile && !project.deriFileInfos) {
      if (project.fileId) {
        await uploadApi.delete(`/upload/files/${project.fileId}`);
      }
      if (project.imageId) {
        await uploadApi.delete(`/upload/files/${project.imageId}`);
      }
    }

    /* ---- Update project data ---- */
    const dataToUpdate: any = {
      title: project.title,
      description: project.description,
      isPublic: project.isPublic,
      price: project.price,
      hasDeriFile: project.deriFileInfos != null,
      isVerified: isTrustedUser,
    };

    if (uploadedDeriFileId !== null) {
      dataToUpdate.deriFile = uploadedDeriFileId;
    }
    if (uploadedImageFileId !== null) {
      dataToUpdate.image = uploadedImageFileId;
    }

    const res = await api.put(`/api/projects/${project.id}`, {
      data: dataToUpdate,
    });

    return res.data.data;
  } catch (e) {
    console.error(e);
    throw e;
  }
};

/* ============================
   CREATE
============================ */

export const createProject = async (project: project, isTrustedUser: boolean) => {
  try {
    let uploadedDeriFileId: number | null = null;
    let uploadedImageFileId: number | null = null;

    if (project.deriFile && project.image) {
      const formData = new FormData();
      formData.append("files", project.deriFile);
      formData.append("files", project.image);

      const uploadRes = await uploadApi.post("/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      uploadRes.data.forEach((file: any) => {
        if (file.mime?.startsWith("image/")) {
          uploadedImageFileId = file.id;
        } else {
          uploadedDeriFileId = file.id;
        }
      });
    }

    const res = await api.post(`/api/projects`, {
      data: {
        title: project.title,
        description: project.description,
        isPublic: project.isPublic,
        price: project.price,
        author: project.author,
        hasDeriFile: uploadedDeriFileId != null,
        deriFile: uploadedDeriFileId,
        image: uploadedImageFileId,
        isVerified: isTrustedUser,
      },
    });

    return res.data.data;
  } catch (e) {
    console.error(e);
    throw e;
  }
};

/* ============================
   DELETE
============================ */

export const deleteProject = async (projectId: string) => {
  try {
    const project = await getProjectById(projectId);

    const res = await api.delete(`/api/projects/${projectId}`);

    if (project.deriFile?.id) {
      await uploadApi.delete(`/upload/files/${project.deriFile.id}`);
    }

    if (project.image?.id) {
      await uploadApi.delete(`/upload/files/${project.image.id}`);
    }

    return res;
  } catch (e) {
    console.error(e);
    throw e;
  }
};
